/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

/**
 * Drops sidebar entries whose module the user was not granted.
 *
 * Hidden, not disabled: the existing `disabled` flag already means "your Permit scope denies this",
 * and showing both states the same way would leave a menu of grey text nobody can read. An entry
 * that survives keeps its `disabled` value untouched, so Permit-based greying still behaves exactly
 * as it does today.
 *
 * An entry declares its module with a `module` key. One without a `module` is not module-owned:
 * a grouping row such as *Logs* keeps whichever children survive and disappears only when none do,
 * and a plain child of an already-gated parent (every report page under *Reports*) is left alone.
 *
 * @param {Array<{id: string, items: Array<object>}>} categories
 * @param {(key: string|null|undefined) => boolean} hasModule
 * @returns {Array<{id: string, items: Array<object>}>} categories with nothing empty left in them
 */
export const filterMenuByModules = (categories, hasModule) => {
  if (!Array.isArray(categories)) return []
  if (typeof hasModule !== 'function') return categories

  // Identity, not length: a row whose own children were filtered comes back as a new object while
  // the count is unchanged, and comparing lengths alone would throw that rebuilt row away. Returning
  // the original object when truly nothing moved is what keeps an untouched branch untouched — the
  // caller rebuilds its menu literal every render, so this buys correctness here, not re-render
  // savings.
  const unchanged = (next, previous) =>
    next.length === previous.length && next.every((entry, index) => entry === previous[index])

  const keepItem = (item) => {
    if (!item || typeof item !== 'object') return null

    if (item.module) return hasModule(item.module) ? item : null

    // No module of its own. If it groups children, it lives or dies with them.
    if (!Array.isArray(item.children)) return item

    const children = item.children.map(keepItem).filter(Boolean)

    if (children.length === 0) return null

    return unchanged(children, item.children) ? item : { ...item, children }
  }

  return categories
    .map((category) => {
      if (!category || !Array.isArray(category.items)) return null

      const items = category.items.map(keepItem).filter(Boolean)

      if (items.length === 0) return null

      return unchanged(items, category.items) ? category : { ...category, items }
    })
    .filter(Boolean)
}

export default filterMenuByModules
